<template>
  <div class="balmer-series-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$1/λ = R_H (1/2² - 1/n²)$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真演示区 -->
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>主量子数 (n)</label>
            <span class="val">{{ activeN }} <small>→</small> 2</span>
          </div>
          <div class="hud-item orange">
            <label>发射波长 (λ)</label>
            <span class="val">{{ calcLambda(activeN).toFixed(1) }} <small>nm</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240" class="spectrum-svg">
          <!-- 连续光谱背景 -->
          <rect x="20" y="80" width="360" height="50" fill="url(#visible-grad)" opacity="0.15" rx="4" />
          
          <!-- 巴耳末谱线族 -->
          <g v-for="n in [3, 4, 5, 6]" :key="'line'+n" class="spectral-line-group" @mouseenter="activeN = n">
             <!-- 辉光效果 -->
             <line :x1="calcX(n)" y1="70" :x2="calcX(n)" y2="140" 
                   :stroke="calcColor(n)" stroke-width="8" 
                   :opacity="activeN === n ? 0.3 : 0" />
             <!-- 核心谱线 -->
             <line :x1="calcX(n)" y1="70" :x2="calcX(n)" y2="140" 
                   :stroke="calcColor(n)" stroke-width="3" 
                   :opacity="activeN === n ? 1 : 0.5" />
             
             <!-- 标注 -->
             <text :x="calcX(n)" y="155" :fill="calcColor(n)" text-anchor="middle" font-size="11" font-weight="900">
               H{{ getGreek(n) }}
             </text>
             <text v-if="activeN === n" :x="calcX(n)" y="65" fill="#fff" text-anchor="middle" font-size="10" font-weight="bold">
               {{ calcLambda(n).toFixed(1) }}nm
             </text>
          </g>

          <!-- 原子模型微缩图 (跃迁示意) -->
          <g transform="translate(320, 180)">
             <circle r="4" fill="#ef4444" />
             <circle r="20" fill="none" stroke="#1e293b" />
             <circle r="35" fill="none" stroke="#1e293b" />
             <!-- 跃迁电子 -->
             <circle :fill="calcColor(activeN)" :r="3" :cx="Math.cos(time)*35" :cy="Math.sin(time)*35">
                <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="3s" repeatCount="indefinite" />
             </circle>
             <path v-if="activeN" d="M 0 -35 L 0 -20" stroke="#fff" stroke-width="1.5" marker-end="url(#arr-down)">
                <animate attributeName="opacity" values="1;0;1" dur="1s" repeatCount="indefinite" />
             </path>
          </g>

          <defs>
             <linearGradient id="visible-grad">
                <stop offset="0%" stop-color="#ff0000" />
                <stop offset="20%" stop-color="#ffff00" />
                <stop offset="40%" stop-color="#00ff00" />
                <stop offset="60%" stop-color="#00ffff" />
                <stop offset="80%" stop-color="#0000ff" />
                <stop offset="100%" stop-color="#ff00ff" />
             </linearGradient>
             <marker id="arr-down" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#fff" />
             </marker>
          </defs>
        </svg>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="row items-center justify-between">
              <span class="label">选择跃迁能级层级 (n)</span>
              <el-radio-group v-model="activeN" size="mini">
                 <el-radio-button :label="3">n=3 (α)</el-radio-button>
                 <el-radio-button :label="4">n=4 (β)</el-radio-button>
                 <el-radio-button :label="5">n=5 (γ)</el-radio-button>
                 <el-radio-button :label="6">n=6 (δ)</el-radio-button>
              </el-radio-group>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>物理规律：</strong>巴耳末系是氢原子电子从高能级（n > 2）跃迁回 <strong>n = 2</strong> 能级时发射出的可见光线。每条谱线对应极精确的频率，构成了氢原子的“条形码”。在恒星光谱分析中，科学家正是通过观测这些谱线的红移或蓝移来测量宇宙的膨胀速度。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BalmerSeriesViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      activeN: 3,
      time: 0
    }
  },
  methods: {
    calcLambda(n) {
      // Rydberg Formula: 1/λ = R_H * (1/4 - 1/n²)
      const Rh = 0.0109737; // per nm
      const invL = Rh * (0.25 - 1 / (n * n));
      return 1 / invL;
    },
    calcX(n) {
      const lam = this.calcLambda(n);
      // Map 400nm -> 380px, 700nm -> 20px
      return 20 + (700 - lam) * 1.25;
    },
    calcColor(n) {
      if (n === 3) return "#f43f5e"; // 656.3nm Red
      if (n === 4) return "#06b6d4"; // 486.1nm Cyan
      if (n === 5) return "#3b82f6"; // 434.0nm Blue
      if (n === 6) return "#8b5cf6"; // 410.2nm Violet
      return "#fff";
    },
    getGreek(n) {
      const greeks = { 3: 'α', 4: 'β', 5: 'γ', 6: 'δ' };
      return greeks[n] || '';
    }
  }
}
</script>

<style scoped lang="scss">
.balmer-series-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #fff1f2; color: #e11d48; border: 1px solid #fecdd3; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #64748b; font-weight: 700; }
      .val { font-size: 14px; color: #f1f5f9; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.orange { border-left: 3px solid #f59e0b; }
    }
  }
}

.spectral-line-group { 
  cursor: pointer;
  line { transition: opacity 0.3s ease; }
  &:hover line { opacity: 1; }
}

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card {
  background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0;
  .row { display: flex; align-items: center; }
  .label { font-size: 12px; color: #64748b; font-weight: 700; }
}

.discovery-card-compact {
  background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px;
  i { color: #f43f5e; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } }
}
</style>
